import { FaArrowLeft } from "react-icons/fa";
import Footer from "./Footer";
import Header from "./header";
import { Helmet } from "react-helmet-async";
const styles = {
  pageWrapper: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    width: "100%",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: "#FFFFFF",
    overflowX: "hidden",
  },
  container: {
    width: "80%",
    maxWidth: "1600px",
    margin: "0 auto",
    padding: "40px 0",
    boxSizing: "border-box",
    animation: "fadeIn 0.5s ease-out forwards",
    flex: "1",
    marginTop: "50px",
  },
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "14px",
    color: "#64748B",
    marginBottom: "25px",
    flexWrap: "wrap",
  },
  backBtn: {
    background: "white",
    border: "1px solid #E2E8F0",
    padding: "8px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: "600",
    color: "#475569",
    transition: "0.2s",
  },
  grid: { display: "grid", gap: "60px", alignItems: "start" },
};
const WarrantyPolicy = ({ onBack }) => {
  return (
    <>
      <Helmet>
        <title>Warranty Policy | CGRMART </title>

        <meta
          name="description"
          content="Read CGRMART warranty policy for EV chargers including coverage, claim process, and terms for AC and DC charging stations."
        />

        <meta
          name="keywords"
          content="warranty policy, EV charger warranty, CGRMART, electric vehicle charger support, warranty terms"
        />

        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="Warranty Policy | CGRMART" />
        <meta
          property="og:description"
          content="Understand warranty coverage, claims, and support for CGRMART EV charging products."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cgrmart.com/warranty-policy" />

        {/* Twitter */}
        <meta name="twitter:title" content="Warranty Policy | CGRMART" />
        <meta
          name="twitter:description"
          content="CGRMART EV charger warranty terms and conditions."
        />

        {/* Canonical */}
        <link rel="canonical" href="https://cgrmart.com/warranty-policy" />
      </Helmet>

      <Header />
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div className="max-w-4xl mx-auto p-6 text-gray-800">
            <h1 className="text-3xl font-bold mb-2">
              CGRMART – Warranty Policy
            </h1>
            <p className="mb-6 text-sm text-gray-500">
              Effective Date: 21 March 2026
            </p>

            {/* 1. Purpose */}
            <h2 className="text-xl font-semibold mt-6 mb-2">1. Purpose</h2>
            <p className="mb-4">
              This Warranty Policy (“Policy”) outlines the terms and conditions
              governing warranty coverage for electric vehicle (EV) charging
              products supplied by CGRMART (“Company”). This Policy ensures
              customers receive repair or replacement support for eligible
              manufacturing defects, subject to the terms and limitations
              described herein.
            </p>

            {/* 2. Warranty Coverage */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              2. Warranty Coverage
            </h2>

            <h3 className="font-semibold mt-4 mb-1">2.1 Products Covered</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>AC Electric Vehicle Chargers (all models)</li>
              <li>DC Fast Electric Vehicle Chargers (all models)</li>
            </ul>

            <h3 className="font-semibold mt-4 mb-1">
              2.2 Duration of Coverage
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Standard warranty period: 12 months from the date of invoice
              </li>
              <li>
                Repaired/replaced components are covered for 3 months or the
                remaining warranty period (whichever is longer)
              </li>
              <li>
                In case of variation, the warranty mentioned in the invoice
                shall prevail
              </li>
            </ul>

            <h3 className="font-semibold mt-4 mb-1">2.3 Scope of Warranty</h3>
            <p className="mb-2">
              This warranty covers manufacturing defects under normal
              installation and usage conditions, including:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Failure of internal electronic components</li>
              <li>Structural or enclosure defects</li>
              <li>Malfunction of safety systems</li>
              <li>
                Display or user interface defects due to manufacturing faults
              </li>
            </ul>

            {/* 3. Exclusions */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              3. Warranty Exclusions
            </h2>
            <ul className="list-disc pl-6 mb-4">
              <li>Misuse, negligence, accidents, or improper handling</li>
              <li>Unauthorised repairs, modifications, or alterations</li>
              <li>Improper installation by non-authorised personnel</li>
              <li>
                Electrical surges, lightning, fire, flooding, or force majeure
                events
              </li>
              <li>Cosmetic damages not affecting functionality</li>
              <li>Use outside intended EV charging purpose</li>
              <li>Failure to follow installation or safety guidelines</li>
              <li>Use of non-approved third-party accessories or cables</li>
            </ul>

            {/* 4. Claim Procedure */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              4. Warranty Claim Procedure
            </h2>

            <h3 className="font-semibold mt-4 mb-1">
              Step 1 – Claim Initiation
            </h3>
            <p className="mb-2">
              Submit claims through the official warranty portal:
            </p>
            <a
              href="https://tuckermotors.com/warranty/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-600 font-semibold underline"
            >
              https://tuckermotors.com/warranty/
            </a>

            <p className="mt-2 mb-2">Required details:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Purchase invoice copy</li>
              <li>Product serial number</li>
              <li>Description of the issue</li>
            </ul>

            <h3 className="font-semibold mt-4 mb-1">
              Step 2 – Claim Assessment
            </h3>
            <p className="mb-4">
              Upon receipt of the claim, the Company’s technical team shall
              review the submitted information to determine eligibility under
              this Policy. The Company reserves the sole right to approve or
              reject claims based on its evaluation.
            </p>

            <h3 className="font-semibold mt-4 mb-1">
              Step 3 – Repair or Replacement
            </h3>
            <p className="mb-4">
              All warranty services shall be carried out exclusively by
              authorised service personnel designated by the Company.
            </p>
            <li>
              Where the defect is determined to be repairable, the product shall
              be repaired.
            </li>
            <li>
              Where repair is not feasible or commercially viable, the Company
              reserves the right to replace the product with a unit of
              equivalent or higher specification{" "}
            </li>
            <p>
              Any replaced components or units shall become the property of the
              Company.
            </p>

            <h3 className="font-semibold mt-4 mb-1">
              Step 4 – Service Timeline
            </h3>
            <li>
              The standard turnaround time for completion of warranty service
              shall be 7 to 15 business days from the date of claim approval,
              subject to the nature of the defect and availability of parts
            </li>

            <li>
              Customers shall be informed of the status and progress of their
              claim through the warranty portal and registered communication
              channels.
            </li>

            {/* 5. Limitation */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              5. Limitation of Liability
            </h2>
            <p className="mb-4">
              <li>
                {" "}
                To the maximum extent permitted under applicable law, the
                Company’s liability under this Policy shall be strictly limited
                to the repair or replacement of defective products as provided
                herein.
              </li>
              <li>
                {" "}
                The Company shall not be liable for any indirect, incidental,
                consequential, or special damages, including but not limited to
                loss of business, loss of revenue, loss of data, or downtime
                arising from product failure.
              </li>
            </p>

            {/* 6. Law */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              6. Governing Law & Jurisdiction
            </h2>
            <p className="mb-4">
              This Policy shall be governed by and construed in accordance with
              the applicable laws of India. Any disputes arising out of or in
              connection with this Policy shall be subject to the exclusive
              jurisdiction of the competent courts in Madurai, Tamil Nadu,
              India.
            </p>

            {/* 7. General */}
            <h2 className="text-xl font-semibold mt-6 mb-2">
              7. General Provisions
            </h2>
            <ul className="list-disc pl-6 mb-4">
              <li>
                {" "}
                This Policy constitutes the entire warranty agreement between
                the Company and the customer.
              </li>
              <li>
                The Company reserves the right to amend, modify, or update this
                Policy at any time without prior notice
              </li>
              <li>
                In case of any inconsistency between this Policy and specific
                contractual terms, the terms specified in the invoice or
                agreement shall prevail.
              </li>
            </ul>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default WarrantyPolicy;
